// @flow
/** @jsx jsx */

import { get } from 'lodash';

import { MessageBar } from '@zapier/common-components';
import { isTierFree } from '@zapier/billing/src/utils';
import { jsx } from '@emotion/core';
import { Border, Colors } from '@zapier/theme';

import AmountDue from './AmountDue';
import AmountDueOnNextInterval from './AmountDueOnNextInterval';
import InvoiceLine from './InvoiceLine';
import Skeleton from './Skeleton';
import useInvoicePreview from './useInvoicePreview';

import type { Plan } from '@zapier/billing/src/types';

type Props = {
  accountId: number,
  plan: Plan,
};

const breakdownCss = {
  borderBottom: `${Border.size.default} solid ${Border.color.default}`,
  padding: 10,
};

const sectionCss = {
  backgroundColor: Colors.white,
  border: `${Border.size.default} solid ${Border.color.default}`,
  borderRadius: Border.radius.default,
  lineHeight: '1.6em',
  overflow: 'hidden',
};

const ChangePlanSummary = ({ accountId, plan }: Props) => {
  const { error, invoice, isLoading } = useInvoicePreview({
    accountId,
    planId: get(plan, 'id'),
  });

  if (isLoading || !invoice) {
    return <Skeleton />;
  }

  if (error || !plan) {
    return <MessageBar type="error">{error}</MessageBar>;
  }

  const tiers = plan.tiers;
  const planMeteredCostPerTask =
    Array.isArray(tiers) && tiers.length > 0
      ? tiers[tiers.length - 1].pricePerTask
      : 0;
  const freeTier = tiers.find(isTierFree);
  const planNumFreeMeteredTasks = freeTier ? freeTier.numTasks : 0;

  return (
    <section css={sectionCss}>
      {invoice.items.length > 0 && (
        <div css={breakdownCss}>
          {invoice.items.map(line => (
            <InvoiceLine key={line.id} line={line} />
          ))}
        </div>
      )}
      <AmountDue amountDue={invoice.amountDue} />
      {plan.amount > 0 && (
        <AmountDueOnNextInterval
          isPlanMetered={tiers.length > 0}
          nextPaymentDate={invoice.periodEnd}
          planBillingAmount={plan.billingAmount}
          planInterval={plan.billingInterval}
          planMeteredCostPerTask={planMeteredCostPerTask}
          planNumFreeMeteredTasks={planNumFreeMeteredTasks}
        />
      )}
    </section>
  );
};

export default ChangePlanSummary;
